import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { renderAtletaVideo, descargarArchivo, slugify } from '@/lib/api';
import { coverCrop, solidBg, optimizeImage, zoomSliderToFactor } from '@/lib/imagen';
import { Preview } from '@/components/Preview';
import { PodioCard } from '@/components/PodioCard';
import { ImageEditor } from '@/components/ImageEditor';
import { CARD_DIMS, type Formato, type PosV, type PosH } from '@/components/AtletaCard';
import { useSession } from '@/store/SessionContext';

const ESCALA_MIN = 0.5;
const ESCALA_MAX = 1.4;

/** Opciones aún no listas (Cliente) visibles solo en local; ocultas en PRO. */
const ES_DEV = import.meta.env.DEV;

export function Editor() {
  const navigate = useNavigate();
  const { seleccion, fotoDataUrl, setFotoDataUrl } = useSession();

  const [formato, setFormato] = useState<Formato>('16:9');
  const [escala, setEscala] = useState(1);
  const [degradado, setDegradado] = useState(50);
  const [opacidad, setOpacidad] = useState(50);
  const [posicion, setPosicion] = useState<PosV>('abajo');
  const [posH, setPosH] = useState<PosH>('izquierda');
  const [branding, setBranding] = useState<PosV>('abajo');
  const [fotoX, setFotoX] = useState(50);
  const [fotoY, setFotoY] = useState(50);
  const [zoom, setZoom] = useState(0);
  const [fotoSrc, setFotoSrc] = useState<string | undefined>(undefined);
  const [cliente, setCliente] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);
  const [logoV, setLogoV] = useState<PosV>('arriba');
  const [logoH, setLogoH] = useState<PosH>('derecha');
  const [logoEscala, setLogoEscala] = useState(1);
  const [busy, setBusy] = useState<'video' | 'imagen' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cardRef = useRef<HTMLDivElement | null>(null);
  const overlayDataRef = useRef<HTMLDivElement | null>(null);
  const overlayLogoRef = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const logoInput = useRef<HTMLInputElement | null>(null);

  const { w, h } = CARD_DIMS[formato];

  // Recorta la foto al recuadro del formato + encuadre (cover) para preview y captura.
  useEffect(() => {
    if (!fotoDataUrl) {
      setFotoSrc(undefined);
      return;
    }
    const { w: tw, h: th } = CARD_DIMS[formato];
    let cancel = false;
    coverCrop(fotoDataUrl, tw, th, fotoX, fotoY, zoomSliderToFactor(zoom))
      .then((src) => { if (!cancel) setFotoSrc(src); })
      .catch(() => { if (!cancel) setFotoSrc(fotoDataUrl); });
    return () => { cancel = true; };
  }, [fotoDataUrl, fotoX, fotoY, zoom, formato]);

  // Sin selección (entró directo o recargó con F5).
  if (!seleccion?.evento) {
    return (
      <div className="ld-card">
        <h2 className="ld-card__title">Sin prueba seleccionada</h2>
        <p className="ld-card__hint">Vuelve al inicio, carga una competición y pulsa «Generar» en una prueba.</p>
        <button type="button" className="ld-btn ld-btn--primary" onClick={() => navigate('/')}>
          Ir al inicio
        </button>
      </div>
    );
  }

  const { evento, competicion, fecha } = seleccion;
  const compeName = competicion || evento.nombre;
  const categoria = `${evento.nombre}${evento.ronda ? ' · ' + evento.ronda : ''}`.toUpperCase();
  const fechaTxt = evento.fecha || fecha;

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = typeof reader.result === 'string' ? reader.result : undefined;
      if (!raw) return;
      // Motor central: optimiza (downscale + JPEG) UNA vez al subir para que el
      // encuadre y los renders no arrastren fotos de móvil de 5-10 MB.
      const optimizada = await optimizeImage(raw).catch(() => raw);
      setFotoDataUrl(optimizada);
    };
    reader.readAsDataURL(f);
  }

  function onPickLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(typeof reader.result === 'string' ? reader.result : undefined);
    reader.readAsDataURL(f);
  }

  const cardProps = {
    competicion: compeName,
    categoria,
    fecha: fechaTxt,
    podio: evento.podio,
    formato,
    escala,
    degradado,
    opacidad,
    posicion,
    posH,
    branding,
    fotoSrc,
    logoDataUrl: cliente ? logoDataUrl : undefined,
    logoV,
    logoH,
    logoEscala,
  };

  async function generarImagen() {
    if (!cardRef.current) return;
    setBusy('imagen');
    setError(null);
    try {
      // Espera a que las webfonts (Bricolage, IBM Plex Mono, etc.) estén
      // cargadas: si no, html2canvas captura con la fuente de respaldo.
      await document.fonts.ready;
      const canvas = await html2canvas(cardRef.current, { width: w, height: h, scale: 1, backgroundColor: null, useCORS: true });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('No se pudo generar la imagen');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slugify(compeName)}_${slugify(evento.nombre)}_${formato.replace(':', 'x')}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function generarVideo() {
    if (!overlayDataRef.current) return;
    setBusy('video');
    setError(null);
    try {
      await document.fonts.ready;
      const capt = async (node: HTMLDivElement) =>
        (await html2canvas(node, { width: w, height: h, scale: 1, backgroundColor: null, useCORS: true })).toDataURL('image/png');
      const layers: { dataUrl: string; side: 'left' | 'right' | null }[] = [];
      // En 16:9 los datos entran desde su lado (posH); en 9:16 (ancho completo)
      // solo fade.
      const dataSide = formato === '16:9' ? (posH === 'derecha' ? 'right' : 'left') : null;
      layers.push({ dataUrl: await capt(overlayDataRef.current), side: dataSide });
      if (cliente && logoDataUrl && overlayLogoRef.current) {
        layers.push({ dataUrl: await capt(overlayLogoRef.current), side: logoH === 'derecha' ? 'right' : 'left' });
      }
      const baseDataUrl = fotoSrc ?? solidBg(w, h);
      const r = await renderAtletaVideo({ baseDataUrl, layers, w, h, dur: 5 });
      await descargarArchivo(r.url, `${slugify(compeName)}_${slugify(evento.nombre)}_${formato.replace(':', 'x')}.mp4`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  const SegBtn = ({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button type="button" className={`ld-btn ld-btn--small ${on ? 'ld-btn--primary' : 'ld-btn--ghost'}`} onClick={onClick}>
      {children}
    </button>
  );
  const lbl = { fontSize: 11, letterSpacing: '0.06em', minWidth: 80 } as const;

  return (
    <div className="ld-stack ld-stack--lg">
      <div className="ld-card">
        <div className="ld-spread">
          <div>
            <p className="muted mono" style={{ margin: 0, fontSize: 11, letterSpacing: '0.06em' }}>{compeName}</p>
            <h2 className="ld-card__title" style={{ margin: '4px 0 0' }}>
              {evento.nombre}
              {evento.ronda ? <span className="muted" style={{ fontWeight: 400 }}> · {evento.ronda}</span> : null}
            </h2>
          </div>
          <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => navigate('/')}>← Volver</button>
        </div>
      </div>

      {/* Vista previa + foto */}
      <div className="ld-card">
        <div className="ld-spread" style={{ marginBottom: 14 }}>
          <h3 className="ld-card__title" style={{ margin: 0 }}>Vista previa</h3>
          <div className="ld-row">
            <input ref={fileInput} type="file" accept="image/*" hidden onChange={onPickFile} />
            <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => fileInput.current?.click()}>
              {fotoDataUrl ? 'Cambiar foto' : 'Subir foto'}
            </button>
            {fotoDataUrl ? (
              <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setFotoDataUrl(undefined)}>Quitar foto</button>
            ) : null}
          </div>
        </div>

        <Preview w={w} h={h}>
          <PodioCard {...cardProps} />
        </Preview>
        <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
          <PodioCard ref={cardRef} {...cardProps} />
          <PodioCard ref={overlayDataRef} {...cardProps} overlay overlayPart="datos" />
          <PodioCard ref={overlayLogoRef} {...cardProps} overlay overlayPart="logo" />
        </div>

        {fotoDataUrl ? (
          <div style={{ marginTop: 14 }}>
            <ImageEditor
              imageUrl={fotoDataUrl} width={w} height={h}
              offX={fotoX} offY={fotoY} zoom={zoom}
              setOffX={setFotoX} setOffY={setFotoY} setZoom={setZoom}
            />
            <p className="muted" style={{ textAlign: 'center', marginTop: 6, fontSize: 11 }}>
              Arrastra para mover · Rueda o pellizco para hacer zoom
            </p>
          </div>
        ) : null}
      </div>

      {/* Ajustes */}
      <div className="ld-card">
        <h3 className="ld-card__title">Ajustes</h3>
        <div className="ld-stack">
          <div className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>FORMATO</span>
            <SegBtn on={formato === '16:9'} onClick={() => setFormato('16:9')}>16:9</SegBtn>
            <SegBtn on={formato === '9:16'} onClick={() => setFormato('9:16')}>9:16</SegBtn>
          </div>
          <div className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>POSICIÓN</span>
            <SegBtn on={posicion === 'arriba'} onClick={() => setPosicion('arriba')}>Arriba</SegBtn>
            <SegBtn on={posicion === 'abajo'} onClick={() => setPosicion('abajo')}>Abajo</SegBtn>
            {formato === '16:9' ? (
              <>
                <span style={{ width: 8 }} />
                <SegBtn on={posH === 'izquierda'} onClick={() => setPosH('izquierda')}>Izquierda</SegBtn>
                <SegBtn on={posH === 'derecha'} onClick={() => setPosH('derecha')}>Derecha</SegBtn>
              </>
            ) : null}
          </div>
          <div className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>BRANDING</span>
            <SegBtn on={branding === 'arriba'} onClick={() => setBranding('arriba')}>Arriba</SegBtn>
            <SegBtn on={branding === 'abajo'} onClick={() => setBranding('abajo')}>Abajo</SegBtn>
          </div>
          <label className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>TAMAÑO</span>
            <input type="range" min={ESCALA_MIN} max={ESCALA_MAX} step={0.05} value={escala} onChange={(e) => setEscala(parseFloat(e.target.value))} style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round(escala * 100)}%</span>
          </label>
          <label className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>DEGRADADO</span>
            <input type="range" min={0} max={100} value={degradado} onChange={(e) => setDegradado(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{degradado}%</span>
          </label>
          <label className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>OPACIDAD</span>
            <input type="range" min={0} max={100} value={opacidad} onChange={(e) => setOpacidad(parseInt(e.target.value, 10))} style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{opacidad}%</span>
          </label>
          {ES_DEV ? (
          <div className="ld-row" style={{ gap: 12 }}>
            <span className="muted mono" style={lbl}>CLIENTE</span>
            <label className="ld-row" style={{ gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={cliente} onChange={() => setCliente((v) => !v)} />
              <span style={{ fontSize: 13 }}>Añadir logo de cliente</span>
            </label>
          </div>
          ) : null}
          {ES_DEV && cliente ? (
            <div className="ld-stack" style={{ gap: 10, paddingLeft: 92 }}>
              <div className="ld-row" style={{ gap: 10 }}>
                <input ref={logoInput} type="file" accept="image/*" hidden onChange={onPickLogo} />
                <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => logoInput.current?.click()}>
                  {logoDataUrl ? 'Cambiar logo' : 'Subir logo'}
                </button>
                {logoDataUrl ? (
                  <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={() => setLogoDataUrl(undefined)}>Quitar logo</button>
                ) : null}
              </div>
              <div className="ld-row" style={{ gap: 12 }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 62 }}>LOGO</span>
                <SegBtn on={logoV === 'arriba'} onClick={() => setLogoV('arriba')}>Arriba</SegBtn>
                <SegBtn on={logoV === 'abajo'} onClick={() => setLogoV('abajo')}>Abajo</SegBtn>
                <span style={{ width: 8 }} />
                <SegBtn on={logoH === 'izquierda'} onClick={() => setLogoH('izquierda')}>Izquierda</SegBtn>
                <SegBtn on={logoH === 'derecha'} onClick={() => setLogoH('derecha')}>Derecha</SegBtn>
              </div>
              <label className="ld-row" style={{ gap: 12 }}>
                <span className="muted mono" style={{ fontSize: 11, letterSpacing: '0.06em', minWidth: 62 }}>TAMAÑO</span>
                <input type="range" min={0.5} max={2.5} step={0.05} value={logoEscala} onChange={(e) => setLogoEscala(parseFloat(e.target.value))} style={{ flex: 1 }} />
                <span className="mono" style={{ fontSize: 11, minWidth: 38, textAlign: 'right' }}>{Math.round(logoEscala * 100)}%</span>
              </label>
            </div>
          ) : null}
        </div>
      </div>

      {/* Generar */}
      <div className="ld-card">
        <h3 className="ld-card__title">Generar</h3>
        <p className="ld-card__hint">La imagen ({formato}) se genera en tu navegador. El vídeo (5 s, {formato}) hace zoom sobre la foto y aparición de los datos.</p>
        <div className="ld-row">
          <button type="button" className="ld-btn ld-btn--primary" disabled={!!busy} onClick={() => void generarImagen()}>
            {busy === 'imagen' ? <><span className="ld-spinner" aria-hidden />Generando…</> : `Descargar imagen ${formato}`}
          </button>
          <button type="button" className="ld-btn ld-btn--ghost" disabled={!!busy} onClick={() => void generarVideo()}>
            {busy === 'video' ? <><span className="ld-spinner" aria-hidden />Generando vídeo…</> : `Descargar vídeo ${formato} (5 s)`}
          </button>
        </div>
        {error ? <div className="ld-login__error" style={{ marginTop: 12 }}>{error}</div> : null}
      </div>
    </div>
  );
}
